import React, { useState, useEffect } from "react";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import Image from "next/image";

/**
 * Use for toggle buttons that require text only. An example for this can be found in the pricing page
 *
 * Props:
 * Value: string[], an array of strings which will be the value of buttons in the component.
 * DefaultValue: string, a string that will be pre-set as the defaultValue.
 * OnValueChange: void function for handling changes in the toggle button.
 *
 */

interface toggleValue {
  text: string;
  value: string;
  imageSource: string;
  selectedImageSource: string;
}
export interface ToggleGroupProps {
  ToggleValues: toggleValue[];
  DefaultValue: string;
  isTransparent: boolean;
  buttonColor: string;
  OnValueChange: (value: string) => void;
}

const ToggleButton = ({
  ToggleValues,
  DefaultValue,
  isTransparent,
  buttonColor,
  OnValueChange,
}: ToggleGroupProps) => {
  const [value, setValue] = useState(DefaultValue);

  useEffect(() => {
    if (DefaultValue != value) {
      setValue(DefaultValue);
    }
  }, [DefaultValue]);

  const toggleGroupItemClasses = `flex w-[80px] sm:w-[110px] h-[32px] 
    rounded-md items-center justify-center leading-4 first:rounded-l last:rounded-r 
    bg-stone-900 text-slate-100 
    hover:shadow-[0_0_0_2px] focus:z-10 checked:outline-none focus:outline-none data-[state=on]:bg-brand-lightblue data-[state=on]:text-slate-100`;

  const transparentToggleGroupItemClasses = `flex w-[100px] sm:w-[120px] h-[32px]
    items-center justify-center leading-4 
    bg-transparent font-bold
    hover:shadow-[0_2px_0_0] focus:z-10 focus:shadow-[0_2px_0_0] data-[state=on]:shadow-[0_2px_0_0] focus:outline-none data-[state=on]:font-bold ${buttonColor}`;

  return (
    <div className="flex w-full justify-center items-end ">
      <ToggleGroup.Root
        className="inline-flex space-x-px rounded e"
        type="single"
        defaultValue={DefaultValue}
        aria-label="Text alignment"
        value={value}
        onValueChange={(newValue) => {
          if (newValue) {
            setValue(newValue);
            OnValueChange(newValue);
          }
        }}
      >
        {ToggleValues.map((toggleValue) => {
          return (
            <ToggleGroup.Item
              className={isTransparent ? transparentToggleGroupItemClasses : toggleGroupItemClasses}
              value={toggleValue.value}
              aria-label="Left aligned"
              key={toggleValue.value}
            >
              {toggleValue.imageSource ? (
                <Image
                  alt="toggle button icon"
                  width={24}
                  height={24}
                  src={
                    toggleValue.value === value
                      ? toggleValue.selectedImageSource
                      : toggleValue.imageSource
                  }
                  className="flex items-center w-[24px] mr-2"
                />
              ) : (
                ""
              )}
              <p>{toggleValue.text}</p>
            </ToggleGroup.Item>
          );
        })}
      </ToggleGroup.Root>
    </div>
  );
};

export default ToggleButton;
